/**
 * useRetentionPreference Hook
 *
 * Manages user-configurable data retention preference via edge function handlers.
 * Reads via `get-preferences` and writes via `update-preferences`.
 *
 * Allowed values: 30, 60, 90 (default), 180, 365 days
 *
 * M4.3: Configurable Data Retention Policies
 * Migrated from Supabase RPCs to data-proxy (Phase 0 PlanetScale migration).
 *
 * DTO migration (Wave 4A): both endpoints are validated through the shared
 * `UserPreferencesDto` / `UpdatePreferencesResultDto` via
 * `fetchJsonValidated`. `retention_days` is now a strict literal union
 * (30 | 60 | 90 | 180 | 365) on the wire — any drift triggers a validation
 * error instead of silently falling back to 90.
 */

import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import {
  UpdatePreferencesResultDto,
  UserPreferencesDto,
  type RetentionDays,
} from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { captureEvent } from '../lib/analytics';

export type { RetentionDays };

export const VALID_RETENTION_DAYS: readonly RetentionDays[] = [30, 60, 90, 180, 365] as const;

// Query key factory — includes userId to prevent cross-user cache pollution
const retentionKeys = {
  all: ['retention-preference'] as const,
  user: (userId: string | null) => ['retention-preference', userId] as const,
};
const DEFAULT_RETENTION_DAYS: RetentionDays = 90;

export function useRetentionPreference() {
  const { accessToken, user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const {
    data: retentionDays = DEFAULT_RETENTION_DAYS,
    isLoading,
    error,
  } = useQuery({
    queryKey: retentionKeys.user(userId),
    queryFn: async (): Promise<RetentionDays> => {
      try {
        const dto = await fetchJsonValidated(
          UserPreferencesDto,
          getEdgeFunctionUrl('get-preferences'),
        );
        return dto.retention_days;
      } catch {
        console.warn('[useRetentionPreference] get-preferences request failed');
        return DEFAULT_RETENTION_DAYS;
      }
    },
    enabled: !!accessToken && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 min (static preference, rarely changes)
    gcTime: 30 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: async (days: RetentionDays): Promise<void> => {
      await fetchJsonValidated(
        UpdatePreferencesResultDto,
        getEdgeFunctionUrl('update-preferences'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ retention_days: days }),
        },
      );
    },
    onSuccess: (_data, newDays) => {
      queryClient.setQueryData(retentionKeys.user(userId), newDays);
    },
  });

  const mutateRef = useRef(mutation.mutateAsync);
  mutateRef.current = mutation.mutateAsync;

  const setRetentionDays = useCallback(
    async (days: RetentionDays) => {
      if (days === retentionDays) return;
      await mutateRef.current(days);
      captureEvent('retention_preference_changed', {
        previous_days: retentionDays,
        new_days: days,
      });
    },
    [retentionDays]
  );

  return {
    retentionDays: accessToken ? retentionDays : DEFAULT_RETENTION_DAYS,
    setRetentionDays,
    isLoading: isLoading && !!accessToken,
    isUpdating: mutation.isPending,
    error: error as Error | null,
    isAuthenticated: !!accessToken,
  };
}
